import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { toast } from "@krea/shared/design-system/utils/toast";

import { queryKeyMap } from "@/features/query-client";
import settings from "@/settings";
import { useFilterStore } from "@/store/filters-menu/hooks/useFilterStore";
import { getTitleAndDescriptionForFilters } from "@/utils";

import { fetchListLenderApplications } from "../../api";
import type { IListLenderApplicationParams } from "../../types/lender-application";

type ResponseType = Awaited<ReturnType<typeof fetchListLenderApplications>>;

export const useGetLenderApplicationListQuery = (
  lenderCompanyId: number,
  params: IListLenderApplicationParams,
) => {
  const filters = useFilterStore((state) => state.filters);
  const { filter, sort, limit } = params;
  // using any here because Zustand returns unknown type
  const querySort =
    sort ||
    settings.defaultListSort[filter as keyof typeof settings.defaultListSort];
  const queryLimit = limit || settings.defaultListLimit;
  const isActiveFilter = filters.includes(filter);

  return useInfiniteQuery<
    ResponseType,
    AxiosError,
    InfiniteData<ResponseType>,
    ReturnType<typeof queryKeyMap.getLenderApplications>,
    Pick<IListLenderApplicationParams, "offset">
  >({
    queryKey: queryKeyMap.getLenderApplications(filter, querySort),
    queryFn: async ({ signal, pageParam }) => {
      try {
        const response = await fetchListLenderApplications(
          lenderCompanyId,
          {
            filter,
            sort: querySort,
            limit: queryLimit,
            ...pageParam,
          },
          signal,
        );

        return response;
      } catch (error) {
        if (error && error instanceof AxiosError) {
          if (error.code !== "ERR_CANCELED") {
            console.error("Failed fetching lender applications data", error);
            toast.error(
              `Failed fetching ${getTitleAndDescriptionForFilters(filter).title.toLowerCase()} data.`,
            );
          }
        }

        throw error;
      }
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.count < (lastPageParam.offset ?? queryLimit)) {
        return undefined;
      }

      return {
        offset: (lastPageParam.offset ?? 0) + queryLimit,
      };
    },
    initialPageParam: {
      offset: 0,
    },
    refetchOnWindowFocus: isActiveFilter, // We are only re-loading lists when the filter is active
    enabled: !!lenderCompanyId,
  });
};
